import { AUTO_STRATEGY_BID_STEP_IN_CENTS, OPTIMAL_BUDGET_IN_CENTS } from 'utils/constants/auction';
import { NEXT_WEEK_FORMATTED, TODAY_FORMATTED } from 'utils/date';

import { toDate } from 'bloko/blocks/calendar/datesHelper';

import type { Action } from 'store/createReducer';
import {
    AuctionCampaignStatus,
    AuctionData,
    AuctionPeriodType,
    BiddingStrategy,
    setAuction,
    setVacancyId,
} from 'store/models/auctionData';

export const getAuctionDataUrl = (vacancyId: string): string => `/api/v1/auction/vacancy/${vacancyId}/data`;

export const getAuctionDataActionCreators = (
    auctionData: AuctionData,
    isDynamicPriceEnabled?: boolean | null
): Action[] => {
    const { bid, budget, vacancy, auctionCampaign } = auctionData;

    let startDate = auctionCampaign?.startAt || null;
    let endDate = auctionCampaign?.endAt || null;
    let biddingStrategy = auctionCampaign?.biddingStrategy || BiddingStrategy.Manual;

    if (!auctionCampaign && isDynamicPriceEnabled) {
        startDate = startDate || TODAY_FORMATTED;
        endDate = endDate || NEXT_WEEK_FORMATTED;
        biddingStrategy = BiddingStrategy.Auto;
    }

    if (
        auctionCampaign &&
        biddingStrategy === BiddingStrategy.Manual &&
        auctionCampaign.campaignStatus !== AuctionCampaignStatus.Active &&
        auctionCampaign.campaignStatus !== AuctionCampaignStatus.ActiveWithDelayedStart
    ) {
        // если дата завершения истекла, мы обнуляем дату для пользователя при возобновлении
        endDate = endDate && toDate(endDate) < toDate(TODAY_FORMATTED) ? null : endDate;
        auctionCampaign.endAt = endDate;
        // если нет даты завершения и дата старта истекла, мы обнуляем дату для пользователя при возобновлении
        startDate = !endDate && startDate && toDate(startDate) < toDate(TODAY_FORMATTED) ? null : startDate;
        auctionCampaign.startAt = startDate;
    }
    const isEndDateExpired = endDate !== null && toDate(endDate) < toDate(TODAY_FORMATTED);

    if (
        auctionCampaign &&
        biddingStrategy === BiddingStrategy.Auto &&
        auctionCampaign.campaignStatus === AuctionCampaignStatus.InactiveDueToEndDate &&
        isEndDateExpired
    ) {
        // если дата завершения истекла, ставим дату окончания через 7 дней от сегодня
        // дату старта ставим сегодня (нужно для возобновления)
        startDate = TODAY_FORMATTED;
        endDate = NEXT_WEEK_FORMATTED;

        auctionCampaign.startAt = startDate;
        auctionCampaign.endAt = endDate;
    }
    const selectedPeriodType =
        auctionCampaign && (auctionCampaign.startAt || auctionCampaign.endAt)
            ? AuctionPeriodType.Manual
            : AuctionPeriodType.Unlimited;

    const initialMinValue = bid.minValue;
    if (biddingStrategy === BiddingStrategy.Auto) {
        bid.minValue += AUTO_STRATEGY_BID_STEP_IN_CENTS;
        // можем заменять значение только для новых РК
        if (!auctionCampaign && bid.value && bid.value < bid.minValue) {
            bid.value = bid.minValue;
        }
    }

    return [
        setAuction({
            ...auctionData,
            startDate,
            endDate,
            bid: { ...bid, initialValue: bid.value as number, initialMinValue },
            budget: {
                ...budget,
                value: auctionCampaign ? budget.value : OPTIMAL_BUDGET_IN_CENTS,
                initialValue: auctionCampaign ? (budget.value as number) : OPTIMAL_BUDGET_IN_CENTS,
            },
            biddingStrategy,
            selectedPeriodType,
            isDatesChanged: false,
        }),
        setVacancyId(String(vacancy.vacancyId)),
    ];
};
